import { getCookie } from "cookies-next";
import Cookies from "js-cookie";

import isPersonalEmail from "../../../utils/isPersonalEmail";

import {
  MarketoFormField,
  FieldState,
  DataType,
  FormValueRetrievalType,
  Field,
  MarketoVisibilityRuleType,
  MarketoVisibilityOperators,
} from "./types";

const getHiddenFieldValue = (
  type: FormValueRetrievalType | undefined,
  parameterName: string | undefined,
) => {
  if (!parameterName || !type) {
    return null;
  }

  if (type === FormValueRetrievalType.QUERY) {
    const urlParams = new URLSearchParams(location.search);
    const paramValue = urlParams.get(parameterName);
    return paramValue;
  }

  if (type === FormValueRetrievalType.COOKIE) {
    return Cookies.get(parameterName);
  }

  return null;
};

/*
 * This is a mapping of the hidden field values to output the same values as the Lead form,
 * which is done in the back end on submission. This does not happen for the Marketo form,
 * hence we do this mapping here.
 */
const HIDDEN_FIELD_VALUE_MAPPINGS = {
  contentType: {
    HIGH_VALUE_FORM: "High Value Form",
    CASE_STUDY: "Case Study",
    ROI_CALCULATOR: "ROI Calculator",
    EVENT: "Event",
    WEBINAR: "Webinar",
    WEBINAR_RECORDING: "Webinar Recording",
    EBOOK: "Ebook",
    WHITEPAPER: "Whitepaper",
    BROCHURE: "Brochure",
    PRODUCT_OVERVIEW_VIDEO: "Product Overview Video",
    CUSTOMER_TESTIMONIAL_VIDEO: "Customer Testimonial Video",
    DEMO_VIDEO: "Demo Video",
    BLOG_POST: "Blog Post",
    ARTICLE: "Article",
  },
  contentTag: {
    BREX_THOUGHT_LEADERSHIP: "Brex Thought Leadership",
    SOLUTIONS_THOUGHT_LEADERSHIP: "Solutions Thought Leadership",
    BREX_PRODUCT: "Brex Product",
    BREX_CUSTOMERS: "Brex Customers",
    BECOME_A_PARTNER: "Become a Partner",
    PARTNER_INTEGRATION_INTEREST: "Partner Integration Interest",
    CONTACT_SALES: "Contact Sales",
    DEMO_REQUESTS: "Demo Request",
    CREATE_ACCOUNT: "Create Account",
    PRICING: "Pricing",
    SOLUTION_OVERVIEW: "Solution Overview",
  },
  pageSegment: {
    VB: "VB",
    MM_ENT: "MM/ENT",
    VB_MM_ENT: "VB/MM/ENT",
    PRY: "Pry",
    BD: "BD",
  },
  brexSolution: {
    EMPOWER: "Empower",
    CARD: "Card",
    CASH: "Cash",
    CARD_AND_CASH: "Card and Cash",
    BUSINESS_ACCOUNT: "Business Account",
    PRY: "Pry",
    VENTURE_DEBT: "Venture Debt",
    DDA: "DDA",
    BREX_BRAND: "Brex Brand",
  },
  brexFeature: {
    SPEND_MANAGEMENT: "Spend Management",
    BILL_PAY: "Bill Pay",
    GLOBAL: "Global",
    BUDGETS: "Budgets",
    TRAVEL: "Travel",
  },
} as const;

// Add a function to format values based on the field key
const formatHiddenFieldValue = (key: string, value: string): string => {
  const mappings =
    HIDDEN_FIELD_VALUE_MAPPINGS[
      key as keyof typeof HIDDEN_FIELD_VALUE_MAPPINGS
    ];
  if (mappings && value in mappings) {
    return mappings[value as keyof typeof mappings];
  }
  return value;
};

// Update the mapHiddenFields function to use the new formatting function
export const mapHiddenFields = (hiddenFields: { [key: string]: string }) => {
  const keysMap = {
    contentType: "Content_Type__c",
    contentTag: "Content_Tag__c",
    webinarId: "Webinar_ID__c",
    videoId: "Video_ID__c",
    eventId: "Event_ID__c",
    pageSegment: "Page_Segment__c",
    brexSolution: "Brex_Solution__c",
    brexFeature: "Brex_Feature__c",
    salesforceCampaign: "salesforce_campaign",
    partnerId: "Partner_ID__c",
  };

  return Object.entries(hiddenFields).map(([key, value]) => ({
    id: keysMap[key as keyof typeof keysMap] || key,
    value: formatHiddenFieldValue(key, value),
  }));
};

export const mapMarketoResponseToState = (
  fieldData: MarketoFormField[],
  customHiddenFields?: { [key: string]: string },
): FieldState => {
  const fields: FieldState = {};

  // First map custom hidden fields if they exist
  const mappedCustomFields = customHiddenFields
    ? mapHiddenFields(customHiddenFields)
    : [];
  const customFieldsMap = mappedCustomFields.reduce((acc, field) => {
    acc[field.id] = field.value;
    return acc;
  }, {} as Record<string, string>);

  // Add Anonymous_ID_Segment__c to all forms if not already present
  if (!customFieldsMap["Anonymous_ID_Segment__c"]) {
    try {
      const anonymousId = getCookie("brexAnonymousId");
      if (anonymousId && typeof anonymousId === "string") {
        customFieldsMap["Anonymous_ID_Segment__c"] = anonymousId;
      }
    } catch (error) {
      console.error("Error getting anonymousId: ", error);
    }
  }

  fieldData.forEach((field: MarketoFormField) => {
    const fieldValues = {
      name: field.id,
      label: field.label ?? "",
      placeholder: field.hintText ?? "",
      required: field.required,
      error: "",
      value: "",
      visibilityRules: field?.visibilityRules,
      ...(field.dataType === (DataType.TEXTAREA || DataType.TEXT) && {
        maxLength: field?.maxLength ?? 10000,
      }),
    };
    switch (field.dataType) {
      case DataType.TEXT:
      case DataType.EMAIL:
      case DataType.URL:
      case DataType.TEXTAREA:
      case DataType.CURRENCY:
      case DataType.TELEPHONE:
        fields[field.id] = {
          ...fieldValues,
          dataType: field.dataType,
        };
        break;
      case DataType.SELECT:
      case DataType.RADIOBUTTONS:
        fields[field.id] = {
          ...fieldValues,
          options: field.fieldMetaData?.values ?? [],
          dataType: field.dataType,
        };
        break;
      case DataType.CHECKBOXES: {
        const initialValue: Array<string> = [];
        field.fieldMetaData?.values?.forEach((option) => {
          if (option.selected) initialValue.push(option.value);
        });
        fields[field.id] = {
          ...fieldValues,
          options: field.fieldMetaData?.values ?? [],
          value: initialValue,
          dataType: field.dataType,
        };
        break;
      }
      case DataType.HIDDEN:
        fields[field.id] = {
          ...fieldValues,
          dataType: field.dataType,
          value:
            // Check for custom hidden field value first
            customFieldsMap[field.id] ??
            getHiddenFieldValue(
              field?.autoFill?.valueFrom as FormValueRetrievalType,
              field?.autoFill?.parameterName,
            ) ??
            field?.autoFill?.value ??
            "",
          error: "", //hidden should never have errors.
        };
        break;
    }
  });

  // Manually add Anonymous_ID_Segment__c field if we have a value for it
  if (customFieldsMap["Anonymous_ID_Segment__c"]) {
    fields["Anonymous_ID_Segment__c"] = {
      name: "Anonymous_ID_Segment__c",
      label: "Anonymous ID Segment",
      required: false,
      error: "",
      value: customFieldsMap["Anonymous_ID_Segment__c"],
      dataType: DataType.HIDDEN,
    };
  }

  return fields;
};

const isValidEmail = (email: string) =>
  /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email);

//validates and returns the necessary errors associated to a field.

export const requiredFieldErrorMessage = "This is a required field.";
export const invalidEmailErrorMessage = "Enter a valid email address";
export const invalidUrlErrorMessage = "Enter valid URL (e.g. www.brex.com)";
export const requiredCheckboxErrorMessage =
  "This is a required field. Check at least one option above.";
export const invalidWorkEmailErrorMessage = "Please use a work email address.";

export const validateField = ({
  fieldValue,
  isRequired,
  type,
  checkWorkEmail = false,
}: {
  fieldValue: string | number | boolean | Array<string> | undefined;
  isRequired: boolean;
  type: DataType;
  checkWorkEmail?: boolean | null;
}) => {
  if (isRequired && !fieldValue) return requiredFieldErrorMessage;

  if (isRequired && typeof fieldValue === "string" && !fieldValue.trim())
    return requiredFieldErrorMessage;

  switch (type) {
    case DataType.EMAIL:
      if (checkWorkEmail && isPersonalEmail(fieldValue as string))
        return invalidWorkEmailErrorMessage;
      if (!isValidEmail(fieldValue as string)) return invalidEmailErrorMessage;
      return null;

    case DataType.URL:
      if (!isRequired && !fieldValue) return null;
      if (
        !/.+\..+/.test(fieldValue as string) ||
        // a bit of a trick to ensure url has only valid characters
        fieldValue !== encodeURI(fieldValue as string)
      ) {
        return invalidUrlErrorMessage;
      }
      return null;

    case DataType.CHECKBOXES: {
      const checkboxListValue = fieldValue as Array<string>;
      if (isRequired && checkboxListValue.length < 1)
        return requiredCheckboxErrorMessage;

      return null;
    }

    default:
      return null;
  }
};

export const filterByVisibilityRules = (
  field:
    | Field
    | (Field & {
        visibilityRules: {
          rules: {
            subjectField: string;
            operator: string;
            values: string[];
          }[];
          ruleType: string;
        };
      }),
  fieldData: FieldState,
): boolean => {
  if (!("visibilityRules" in field) || !field.visibilityRules?.rules?.length) {
    return true;
  }

  const {
    IS,
    IS_NOT,
    IS_EMPTY,
    IS_NOT_EMPTY,
    STARTS_WITH,
    NOT_STARTS_WITH,
    CONTAINS,
    NOT_CONTAINS,
  } = MarketoVisibilityOperators;

  const rulesResult = field.visibilityRules.rules.every((rule) => {
    const subjectField = fieldData[rule.subjectField];

    if (!subjectField) return false;

    const subjectValue = subjectField.value;
    const isArrayValue = Array.isArray(subjectValue);
    const isSelectOrRadio =
      subjectField.dataType === DataType.SELECT ||
      subjectField.dataType === DataType.RADIOBUTTONS;

    // Check if the subject field's value matches any of the rule's values  (for text, textarea, email, url, telephone, currency)
    const check = (condition: (value: string) => boolean) => {
      if (isArrayValue) {
        return subjectValue.some(condition);
      }
      if (isSelectOrRadio) {
        return condition(subjectValue);
      }
      return condition(subjectValue);
    };

    switch (rule.operator) {
      // Show the field if the subject field's value matches any of the rule's values
      case IS:
        return Array.isArray(subjectValue)
          ? subjectValue.some((v) => rule.values.includes(v))
          : rule.values.includes(subjectValue);
      // Hide the field if the subject field's value does not match any of the rule's values
      case IS_NOT:
        return Array.isArray(subjectValue)
          ? !subjectValue.some((v) => rule.values.includes(v))
          : !rule.values.includes(subjectValue);
      // Show the field if the subject field's value is empty
      case IS_EMPTY:
        return !subjectValue;
      // Hide the field if the subject field's value is not empty
      case IS_NOT_EMPTY:
        return !!subjectValue;
      // Show the field if the subject field's value starts with any of the rule's values
      case STARTS_WITH:
        return check((value) => rule.values.some((v) => value.startsWith(v)));
      // Hide the field if the subject field's value does not start with any of the rule's values
      case NOT_STARTS_WITH:
        return check((value) => !rule.values.some((v) => value.startsWith(v)));
      // Show the field if the subject field's value contains any of the rule's values
      case CONTAINS:
        return check((value) => rule.values.some((v) => value.includes(v)));
      // Hide the field if the subject field's value does not contain any of the rule's values
      case NOT_CONTAINS:
        return check((value) => !rule.values.some((v) => value.includes(v)));
      default:
        return true;
    }
  });

  const isVisible =
    field.visibilityRules.ruleType === MarketoVisibilityRuleType.SHOW
      ? rulesResult
      : !rulesResult;

  return isVisible;
};
